import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  MessageSquare, MapPin, Clock, Star, Users, User,
  ChevronDown, ChevronUp, BarChart3
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EventFeedbackContent() {
  const [feedbackCodes, setFeedbackCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventFeedback, setEventFeedback] = useState(null);
  const [expandedCodes, setExpandedCodes] = useState({});

  const fetchFeedbackCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feedback/all`);
      setFeedbackCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback codes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbackCodes();
  }, [fetchFeedbackCodes]);

  const viewEventFeedback = async (code) => {
    try {
      const response = await axios.get(`${API}/feedback/event/${code}`);
      setEventFeedback(response.data);
      setSelectedEvent(code);
    } catch (error) {
      console.error('Failed to fetch event feedback:', error);
    }
  };

  const formatDateRange = (dates) => {
    if (!dates || dates.length === 0) return '';
    const sorted = [...dates].sort();
    if (sorted.length === 1) {
      try { return format(parseISO(sorted[0]), 'EEE, MMM d, yyyy'); } catch { return sorted[0]; }
    }
    try {
      return `${format(parseISO(sorted[0]), 'MMM d')} - ${format(parseISO(sorted[sorted.length - 1]), 'MMM d, yyyy')}`;
    } catch { return `${sorted[0]} - ${sorted[sorted.length - 1]}`; }
  };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={cn("w-3.5 h-3.5", star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30")} />
      ))}
    </div>
  );

  const renderAvgStars = (rating) => (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} className={cn("w-4 h-4", star <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30")} />
        ))}
      </div>
      <span className="text-sm text-muted-foreground">({rating})</span>
    </div>
  );

  if (loading) {
    return <div className="p-6 text-center animate-pulse text-muted-foreground">Loading...</div>;
  }

  return (
      <div className="space-y-6" data-testid="feedback-dashboard">

        {feedbackCodes.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Feedback Yet</h3>
              <p className="text-sm text-muted-foreground">Generate QR badges from the Upcoming Shifts page to start collecting feedback.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {feedbackCodes.map((fc) => (
              <Card key={fc.code} className="bg-card border-border rounded-sm hover:border-primary/50 transition-colors" data-testid={`feedback-event-${fc.code}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <Badge className="bg-primary/20 text-primary border-primary/30 border">
                      {fc.feedback_count || 0} feedback{(fc.feedback_count || 0) !== 1 ? 's' : ''}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">{fc.code}</span>
                  </div>

                  <div className="space-y-1.5">
                    <p className="text-base font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />{fc.location}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Clock className="w-4 h-4" />{formatDateRange(fc.dates)} | {fc.start_time} - {fc.end_time}
                    </p>
                  </div>

                  {fc.feedback_count > 0 && (() => {
                    const cats = [fc.avg_uniform, fc.avg_engagement, fc.avg_information, fc.avg_communication, fc.avg_professionalism]
                      .map(Number).filter((v) => !isNaN(v) && v > 0);
                    const overall = cats.length ? Math.round((cats.reduce((a, b) => a + b, 0) / cats.length) * 10) / 10 : 0;
                    return (
                      <div className="flex items-center justify-between p-2.5 bg-accent/50 rounded-sm">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Overall rating</span>
                        {renderAvgStars(overall)}
                      </div>
                    );
                  })()}

                  <Button
                    variant="outline"
                    className="w-full border-border rounded-sm"
                    onClick={() => viewEventFeedback(fc.code)}
                    data-testid={`view-feedback-${fc.code}`}
                  >
                    <BarChart3 className="w-4 h-4 mr-2" /> View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Feedback Detail Dialog */}
        <Dialog open={!!selectedEvent} onOpenChange={(open) => { if (!open) { setSelectedEvent(null); setEventFeedback(null); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">Event Feedback</DialogTitle>
            </DialogHeader>
            {eventFeedback && (
              <div className="space-y-4 mt-4">
                {/* Event Info */}
                <div className="p-3 bg-accent/50 rounded-sm space-y-1">
                  <p className="font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />{eventFeedback.event.location}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" />{formatDateRange(eventFeedback.event.dates)} | {eventFeedback.event.start_time} - {eventFeedback.event.end_time}</p>
                </div>

                {/* Averages */}
                {eventFeedback.total > 0 && (
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm space-y-2">
                    <p className="text-sm font-medium uppercase tracking-wider text-primary flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Average Ratings ({eventFeedback.total} responses)</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                      <div className="text-center space-y-1">
                        <p className="text-xs text-muted-foreground uppercase">Uniform</p>
                        {renderAvgStars(eventFeedback.averages.uniform)}
                      </div>
                      <div className="text-center space-y-1">
                        <p className="text-xs text-muted-foreground uppercase">Engagement</p>
                        {renderAvgStars(eventFeedback.averages.engagement)}
                      </div>
                      <div className="text-center space-y-1">
                        <p className="text-xs text-muted-foreground uppercase">Information</p>
                        {renderAvgStars(eventFeedback.averages.information)}
                      </div>
                      <div className="text-center space-y-1">
                        <p className="text-xs text-muted-foreground uppercase">Communication</p>
                        {renderAvgStars(eventFeedback.averages.communication)}
                      </div>
                      <div className="text-center space-y-1">
                        <p className="text-xs text-muted-foreground uppercase">Professionalism</p>
                        {renderAvgStars(eventFeedback.averages.professionalism)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Individual feedbacks */}
                <div className="space-y-2">
                  <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">All Feedback</p>
                  {eventFeedback.feedbacks.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">No feedback submitted yet.</p>
                  ) : (
                    eventFeedback.feedbacks.map((fb) => (
                      <Card key={fb.id} className="bg-accent/30 border-border rounded-sm">
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <Badge variant="outline" className={cn("text-xs", fb.reviewer_type === 'staff' ? "border-blue-500/50 text-blue-400" : "border-green-500/50 text-green-400")}>
                              <User className="w-3 h-3 mr-1" />
                              {fb.reviewer_type === 'staff' ? 'Staff' : 'Public'}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {(() => { try { return format(parseISO(fb.submitted_at), 'MMM d, yyyy HH:mm'); } catch { return fb.submitted_at; } })()}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            <div className="space-y-0.5">
                              <p className="text-xs text-muted-foreground">Uniform</p>
                              {renderStars(fb.uniform_rating)}
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-xs text-muted-foreground">Engagement</p>
                              {renderStars(fb.engagement_rating)}
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-xs text-muted-foreground">Information</p>
                              {renderStars(fb.information_rating)}
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-xs text-muted-foreground">Communication</p>
                              {renderStars(fb.communication_rating)}
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-xs text-muted-foreground">Professionalism</p>
                              {renderStars(fb.professionalism_rating)}
                            </div>
                          </div>
                          {fb.comment && (
                            <p className="text-sm text-foreground bg-accent/50 p-2 rounded-sm italic">"{fb.comment}"</p>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => { setSelectedEvent(null); setEventFeedback(null); }} className="border-border rounded-sm">Close</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
}
